import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ttStatsAPI } from '../services/api';
import { Trophy, Calendar, MapPin, Users } from 'lucide-react';

const TournamentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [tournamentData, setTournamentData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadTournamentData(decodeURIComponent(id));
    }
  }, [id]);

  const loadTournamentData = async (url: string) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await ttStatsAPI.getTournamentDetails(url);
      setTournamentData(data);
    } catch (err) {
      console.error('Error loading tournament data:', err);
      setError('Failed to load tournament data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading tournament details...</p>
        </div>
      </div>
    );
  }

  if (error || !tournamentData) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <p className="text-red-600">{error || 'Tournament data not found'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tournament Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-4">
          <div className="bg-yellow-100 p-3 rounded-xl">
            <Trophy className="h-8 w-8 text-yellow-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{tournamentData.name || 'Tournament'}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
              {tournamentData.date && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {new Date(tournamentData.date).toLocaleDateString()}
                </span>
              )}
              {tournamentData.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {tournamentData.location}
                </span>
              )}
              {tournamentData.participants && (
                <span className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  {tournamentData.participants} participants
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tournament Details */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Tournament Information</h2>
        
        {tournamentData.categories && tournamentData.categories.length > 0 && (
          <div className="mb-6">
            <h3 className="text-md font-medium text-gray-700 mb-3">Categories</h3>
            <div className="flex flex-wrap gap-2">
              {tournamentData.categories.map((category: string, index: number) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                >
                  {category}
                </span>
              ))}
            </div>
          </div>
        )}

        {tournamentData.results && tournamentData.results.length > 0 && (
          <div>
            <h3 className="text-md font-medium text-gray-700 mb-3">Results</h3>
            <div className="space-y-3">
              {tournamentData.results.map((result: any, index: number) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{result.category}</p>
                      <div className="mt-2 space-y-1">
                        {result.winners && result.winners.map((winner: any, wIndex: number) => (
                          <div key={wIndex} className="flex items-center gap-2">
                            <span className={`font-bold ${
                              wIndex === 0 ? 'text-yellow-500' :
                              wIndex === 1 ? 'text-gray-400' :
                              wIndex === 2 ? 'text-orange-600' :
                              'text-gray-700'
                            }`}>
                              {wIndex + 1}.
                            </span>
                            <span className="text-gray-900">{winner.name}</span>
                            {winner.club && (
                              <span className="text-sm text-gray-500">({winner.club})</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!tournamentData.categories && !tournamentData.results && (
          <p className="text-center py-8 text-gray-500">
            Detailed tournament information is not yet available
          </p>
        )}
      </div>
    </div>
  );
};

export default TournamentPage;